import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { HowYouEarnCard, TierMeaningsCard } from '@/components/HowYouEarn';
import { PayoutDisclaimer } from '@/components/PayoutDisclaimer';
import { useAuth } from '@/contexts/AuthContext';

const EXAMPLES = [
  { project: 'Basic Business Website', value: '$2,500', level: 'Warm Introduction', rate: '10%', payout: '$250' },
  { project: 'Custom Website Build', value: '$5,000', level: 'Qualified Referral', rate: '15%', payout: '$750' },
  { project: 'Web Application', value: '$10,000', level: 'Qualified Referral', rate: '15%', payout: '$1,500' },
  { project: 'Web3 / Blockchain Project', value: '$15,000', level: 'Active Closing Partner', rate: '20%', payout: '$3,000' },
  { project: 'Large Custom Platform', value: '$50,000', level: 'Active Closing Partner', rate: '20%', payout: '$10,000' },
  { project: 'Follow-on: AI Automation (existing client)', value: '$8,000', level: 'Additional Service', rate: '10%', payout: '$800' },
  { project: 'Monthly Maintenance Plan', value: '$750/mo', level: 'Recurring Work', rate: '5% × 12', payout: 'up to $450' },
];

function ProgramContent() {
  const { currentUser } = useAuth();

  return (
    <div className="affiliate-program">
      {/* Hero — full width */}
      <div className="terminal-card affiliate-hero">
        <div className="card-header">
          <span className="card-title">GRID AFFILIATE REFERRAL PROGRAM</span>
        </div>
        <div className="card-body">
          <p className="prose">
            GRID is opening referral opportunities for partners who help bring in new Web2 and Web3 business —
            websites, applications, automation tools, audits, blockchain projects, and ongoing support work.
          </p>
          <p className="prose prose--muted">
            Affiliates are paid when a referred client turns into paid work with GRID.
          </p>
          <div className="affiliate-pitch">
            <strong>Yes — the commissions are high, and that's deliberate.</strong> We'd rather put real money
            in the hands of the people who help GRID grow than burn it on advertising. You're not a referral
            link — you're part of GRID's sales force. You open the door; we close and deliver; we both get
            paid from the same win. The more business you bring in, the more you earn, right alongside us.
          </div>
          <Link
            to={currentUser ? '/affiliates' : '/login'}
            className="action-btn large"
            style={{ display: 'inline-block', textDecoration: 'none' }}
          >
            {currentUser ? 'Get Your Affiliate Code' : 'Sign In to Get Your Code'}
          </Link>
        </div>
      </div>

      {/* Details — balanced two columns */}
      <div className="affiliate-grid">
      <div className="affiliate-col">
      {/* How you earn */}
      <HowYouEarnCard />
      </div>

      <div className="affiliate-col">
      {/* What each tier means */}
      <TierMeaningsCard />

      {/* Example payouts */}
      <div className="terminal-card">
        <div className="card-header">
          <span className="card-title">EXAMPLE PAYOUTS</span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-wrap" style={{ border: 'none' }}>
            <table className="terminal-table table-readable">
              <thead>
                <tr>
                  <th>PROJECT TYPE</th>
                  <th>PROJECT VALUE</th>
                  <th>REFERRAL LEVEL</th>
                  <th>COMMISSION</th>
                  <th style={{ textAlign: 'right' }}>YOUR PAYOUT</th>
                </tr>
              </thead>
              <tbody>
                {EXAMPLES.map((e) => (
                  <tr key={e.project}>
                    <td style={{ color: 'var(--text)' }}>{e.project}</td>
                    <td className="cell-tight" style={{ color: 'var(--muted)' }}>{e.value}</td>
                    <td style={{ color: 'var(--muted)' }}>{e.level}</td>
                    <td className="cell-tight" style={{ color: 'var(--muted)' }}>{e.rate}</td>
                    <td className="cell-tight" style={{ textAlign: 'right', color: 'var(--success)', fontWeight: 700 }}>{e.payout}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '16px', display: 'grid', gap: '10px' }}>
            <p className="prose prose--sm">
              <strong style={{ color: 'var(--accent)' }}>Additional services stack:</strong> refer a client for a $5,000 website (Qualified Referral, 15% = $750), then they add $8,000 of AI automation (Additional Service, 10% = $800) — every further stage pays a flat 10% on top of your original commission, for <strong>$1,550</strong> and climbing as they take on bridging and more.
            </p>
            <p className="prose prose--sm">
              <strong style={{ color: 'var(--accent)' }}>Recurring stacks on top:</strong> a $10,000 build (Qualified Referral, 15% = $1,500) plus $750/month maintenance (5% = $37.50/mo, up to $450 over 12 months) — up to <strong>$1,950</strong> total.
            </p>
            <p className="prose prose--sm">
              <strong style={{ color: 'var(--accent)' }}>Large Deal Bonus:</strong> a $50,000 project closed as Active Closing Partner (20% = $10,000) plus 2% on the $25,000 above $25k ($500) — <strong>$10,500</strong> total.
            </p>
          </div>
          <p className="prose prose--sm prose--muted" style={{ marginTop: '12px' }}>
            Examples are illustrative only and are not typical, promised, or guaranteed earnings. Actual
            commission depends on the deals you refer and the revenue GRID actually collects. Participation
            is governed by the{' '}
            <Link to="/affiliate-terms" style={{ color: 'var(--gold)' }}>Affiliate Agreement</Link>.
          </p>
        </div>
      </div>
      </div>
      </div>

      {/* How to refer the right way */}
      <div className="terminal-card" style={{ marginTop: '18px' }}>
        <div className="card-header">
          <span className="card-title">HOW TO REFER THE RIGHT WAY</span>
        </div>
        <div className="card-body">
          <p className="prose prose--sm">
            The best referrals come from <strong style={{ color: 'var(--accent)' }}>real relationships — not cold outreach.</strong> You
            open doors with people who know and trust you; GRID takes it from there. Here's how to earn while
            keeping everything clean and legal, anywhere in the world.
          </p>
          <div className="affiliate-grid" style={{ marginTop: '8px' }}>
            <div>
              <div className="field-label" style={{ color: 'var(--success)' }}>DO</div>
              <ul className="rules-list">
                <li>Talk to people you already know — friends, family, and business owners you deal with.</li>
                <li>Refer local businesses you use and trust in your own town.</li>
                <li>Have a real conversation first — in person, a call, or a message to someone who knows you.</li>
                <li><strong>Share your referral link</strong> — the prospect fills in their own details, which means they've asked us to reach out.</li>
                <li>If you post publicly, add a quick line that you may earn a commission.</li>
              </ul>
            </div>
            <div>
              <div className="field-label" style={{ color: 'var(--danger)' }}>DON'T</div>
              <ul className="rules-list">
                <li>Don't cold-email or cold-text strangers, or buy/scrape contact lists.</li>
                <li>Don't contact anyone who hasn't agreed to hear from GRID.</li>
                <li>Don't quote prices, promise timelines, or guarantee outcomes — that's GRID's job.</li>
                <li>Don't use spam, bots, fake accounts, or pressure tactics.</li>
              </ul>
            </div>
          </div>
          <p className="prose prose--sm" style={{ marginTop: '8px' }}>
            <strong style={{ color: 'var(--accent)' }}>One rule to remember:</strong> only reach out to people who've
            agreed to be contacted — or just share your link and let them raise their hand themselves. You open the
            door; we close and deliver; we both get paid.
          </p>
        </div>
      </div>

      <div style={{ marginTop: '18px' }}>
        <PayoutDisclaimer />
      </div>

      <div style={{ textAlign: 'center', margin: '20px 0 0' }}>
        <Link
          to={currentUser ? '/affiliates' : '/login'}
          className="action-btn large"
          style={{ display: 'inline-block', textDecoration: 'none' }}
        >
          {currentUser ? 'Get Your Affiliate Code' : 'Sign In to Get Your Code'}
        </Link>
      </div>
    </div>
  );
}

export function AffiliateProgramPage() {
  return (
    <Layout wide>
      <ProgramContent />
    </Layout>
  );
}
